import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useBooking } from "../context/BookingContext";
import { usePricing } from "../context/PricingContext";
import ovalGround from "../images/oval-ground.jpg";
import boxCricket from "../images/box.jpg";
import pickleball from "../images/pickleball.jpg";
import img1647 from "../images/IMG_1647.jpg";
import img1649 from "../images/IMG_1649.jpg";
import img1650 from "../images/IMG_1650.jpg";
import img1653 from "../images/IMG_1653.jpg";
import img1654 from "../images/IMG_1654.jpg";
import img1662 from "../images/IMG_1662.jpg";
import img1663 from "../images/IMG_1663.jpg";
import img1664 from "../images/IMG_1664.jpg";

// ─── Animation helpers ────────────────────────────────────────────────────────
const fadeUp = (delay = 0, duration = 0.65) => ({
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration, delay, ease: [0.22, 1, 0.36, 1] },
});

// ─── Data ─────────────────────────────────────────────────────────────────────
const COURTS = [
  {
    name: "Oval Cricket Ground",
    courtKey: "Oval Cricket Ground",
    tag: "1 Full-Size Ground",
    image: ovalGround,
    desc: "Professional hybrid turf with LED floodlights. Perfect for group bookings and friends.",
    features: ["Hybrid turf pitch", "LED floodlights", "Safety netting", "Huge boundary space"],
    note: "Fresh ball provided at start of each game. Replacement balls available at ₹50 each.",
  },
  {
    name: "Box Cricket",
    courtKey: "Box Cricket Court 1",
    tag: "2 Enclosed Courts",
    image: boxCricket,
    desc: "Artificial turf, fully enclosed. Perfect for corporate events and group bookings.",
    features: ["Artificial turf", "Safety netting", "LED night lights", "Bats and balls provided"],
    note: "Fresh ball provided at start of each game. Replacement balls available at ₹50 each.",
  },
  {
    name: "Pickleball",
    courtKey: "Pickleball Court 1",
    tag: "2 Courts",
    image: pickleball,
    desc: "Dedicated pickleball courts with professional lines, nets, and equipment rental available.",
    features: ["Pro-grade surface", "Equipment rental", "Coaching available", "Floodlit"],
    note: "Fresh ball provided at start of each game. Replacement balls available at ₹50 each.",
  },
];

const STEPS = [
  {
    num: "01",
    icon: "🏟️",
    title: "Pick a Court",
    desc: "Choose from our oval cricket ground, box cricket courts, or pickleball courts.",
  },
  {
    num: "02",
    icon: "📅",
    title: "Choose Your Slot",
    desc: "Browse available 1-hour slots across the next 30 days. Real-time availability.",
  },
  {
    num: "03",
    icon: "⚡",
    title: "Pay & Play",
    desc: "Secure checkout via Razorpay. Instant confirmation. Show up and play.",
  },
];

const PRICING_ROWS = [
  { sport: "🏟️ Oval Cricket", courtKey: "Oval Cricket Ground" },
  { sport: "🏏 Box Cricket",  courtKey: "Box Cricket Court 1"  },
  { sport: "🏓 Pickleball",   courtKey: "Pickleball Court 1"   },
];

// ─── Sections ─────────────────────────────────────────────────────────────────

// ── Availability strip helpers ────────────────────────────────────────────────
// Total bookable court-slot-hours: Oval(1) + Box(2) + Pickleball(2) = 5 courts × 24 slots = 120
const TOTAL_COURT_SLOT_HOURS = 120;
const SPORT_COURTS = { "Oval Cricket": 1, "Box Cricket": 2, "Pickleball": 2 };
const DAY_SHORT  = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const MONTH_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function localDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

function prevDateStr(dateStr) {
  const [y, m, day] = dateStr.split("-").map(Number);
  return localDateStr(new Date(y, m - 1, day - 1));
}

function isBlockForSport(bl, sport) {
  const courts = bl.courts || ["all"];
  if (courts.includes("all")) return true;
  if (sport === "Box Cricket")  return courts.some((c) => c.startsWith("Box Cricket"));
  if (sport === "Pickleball")   return courts.some((c) => c.startsWith("Pickleball"));
  return courts.some((c) => c === "Oval Cricket" || c.startsWith("Oval"));
}

function blockCourtsForSport(bl, sport) {
  const courts = bl.courts || ["all"];
  const total = SPORT_COURTS[sport] || 1;
  if (courts.includes("all")) return total;
  if (sport === "Box Cricket")  return courts.filter((c) => c.startsWith("Box Cricket")).length;
  if (sport === "Pickleball")   return courts.filter((c) => c.startsWith("Pickleball")).length;
  return 1;
}

function getAvailability(dateStr, bookings, blocks) {
  let usedSlotHours = 0;

  for (const sport of Object.keys(SPORT_COURTS)) {
    // Only look at bookings/blocks dated dateStr.
    // b.slots are calendar-day dateStr; b.nextDaySlots are calendar-day dateStr+1.
    // prevDate records' nextDaySlots point to dateStr (not dateStr+1) so must not be counted here.
    const active = (bookings || []).filter((b) =>
      b.date === dateStr && b.sport === sport && !["cancelled","failed"].includes(b.status)
    );
    active.forEach((b) => { usedSlotHours += (b.slots || []).length + (b.nextDaySlots || []).length; });

    const blToday = (blocks || []).filter((bl) => bl.date === dateStr && isBlockForSport(bl, sport));
    blToday.forEach((bl) => {
      const n = blockCourtsForSport(bl, sport);
      usedSlotHours += ((bl.slots || []).length + (bl.nextDaySlots || []).length) * n;
    });
  }

  const free = Math.max(0, TOTAL_COURT_SLOT_HOURS - usedSlotHours);
  if (free <= 0)  return { label: "Full",         color: "#ef4444", bg: "#fef2f2", dot: "#ef4444" };
  if (free <= 40) return { label: `${free} left`, color: "#d97706", bg: "#fffbeb", dot: "#f59e0b" };
  return           { label: `${free} left`,       color: "#16a34a", bg: "#f0fdf4", dot: "#22c55e" };
}

function AvailabilityStrip({ onDateSelect }) {
  const { bookings, blocks } = useBooking();
  const today = new Date();
  const days = Array.from({ length: 14 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    return d;
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="bg-white border-t border-b border-black/6"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-[11px] font-black text-black/40 uppercase tracking-[0.18em]">Next 14 Days</span>
          <div className="flex items-center gap-3 text-[10px] font-semibold text-black/40">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block"/>Available</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block"/>Limited</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400 inline-block"/>Full</span>
          </div>
        </div>
        <div className="flex gap-2.5 overflow-x-auto pb-1" style={{ scrollbarWidth: "none" }}>
          {days.map((d, i) => {
            const dateStr = localDateStr(d);
            const avail   = getAvailability(dateStr, bookings, blocks);
            const isToday = i === 0;
            return (
              <button
                key={dateStr}
                onClick={() => onDateSelect(dateStr)}
                className="flex-shrink-0 flex flex-col items-center gap-1.5 px-4 py-3 rounded-2xl border transition-all duration-150 hover:-translate-y-0.5 hover:shadow-md group"
                style={{
                  borderColor: isToday ? "#f59e0b" : "rgba(0,0,0,0.07)",
                  background: isToday ? "#fffbeb" : "white",
                  minWidth: 76,
                }}
              >
                <span className="text-[10px] font-bold uppercase tracking-wider"
                  style={{ color: isToday ? "#d97706" : "rgba(0,0,0,0.35)" }}>
                  {isToday ? "Today" : DAY_SHORT[d.getDay()]}
                </span>
                <span className="text-xl font-black text-black leading-none">
                  {d.getDate()}
                </span>
                <span className="text-[10px] text-black/30 -mt-0.5">
                  {MONTH_SHORT[d.getMonth()]}
                </span>
                {/* Availability indicator */}
                <div className="flex items-center gap-1 mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold"
                  style={{ background: avail.bg, color: avail.color }}>
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: avail.dot }} />
                  {avail.label}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}

const CAROUSEL_IMAGES = [
  { src: ovalGround, alt: "Oval Cricket Ground" },
  { src: boxCricket, alt: "Box Cricket" },
  { src: pickleball, alt: "Pickleball Courts" },
];

function HeroCarousel() {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % CAROUSEL_IMAGES.length), 3000);
    return () => clearInterval(t);
  }, []);

  const prev = () => setIdx((i) => (i - 1 + CAROUSEL_IMAGES.length) % CAROUSEL_IMAGES.length);
  const next = () => setIdx((i) => (i + 1) % CAROUSEL_IMAGES.length);

  return (
    <div style={{ position: "relative", maxWidth: 480, width: "100%" }}>
      {/* Gold border wrapper */}
      <div
        style={{
          padding: 4,
          background: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #92400e 100%)",
          borderRadius: 20,
          boxShadow: "0 24px 60px rgba(245,158,11,0.15), 0 8px 20px rgba(0,0,0,0.10)",
        }}
      >
        {/* Image container */}
        <div
          style={{
            position: "relative",
            borderRadius: 16,
            overflow: "hidden",
            aspectRatio: "4 / 3",
            background: "#1a1a1a",
          }}
        >
          {CAROUSEL_IMAGES.map((img, i) => (
            <img
              key={img.src}
              src={img.src}
              alt={img.alt}
              style={{
                position: "absolute",
                top: 0, left: 0,
                width: "100%", height: "100%",
                objectFit: "cover",
                objectPosition: "center",
                opacity: i === idx ? 1 : 0,
                zIndex: i === idx ? 2 : 1,
                transition: "opacity 0.6s ease",
              }}
            />
          ))}
          {/* Slide label */}
          <div
            style={{
              position: "absolute",
              bottom: 12,
              left: "50%",
              transform: "translateX(-50%)",
              background: "rgba(0,0,0,0.5)",
              backdropFilter: "blur(6px)",
              borderRadius: 999,
              padding: "4px 14px",
              color: "rgba(255,255,255,0.85)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.08em",
              whiteSpace: "nowrap",
            }}
          >
            {CAROUSEL_IMAGES[idx].alt}
          </div>
        </div>
      </div>

      {/* Prev button */}
      <button
        onClick={prev}
        style={{
          position: "absolute",
          left: -16,
          top: "50%",
          transform: "translateY(-50%)",
          width: 36,
          height: 36,
          borderRadius: "50%",
          background: "white",
          border: "1.5px solid rgba(0,0,0,0.10)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          fontSize: 14,
          color: "#333",
          zIndex: 2,
        }}
        aria-label="Previous image"
      >
        ‹
      </button>

      {/* Next button */}
      <button
        onClick={next}
        style={{
          position: "absolute",
          right: -16,
          top: "50%",
          transform: "translateY(-50%)",
          width: 36,
          height: 36,
          borderRadius: "50%",
          background: "white",
          border: "1.5px solid rgba(0,0,0,0.10)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          fontSize: 14,
          color: "#333",
          zIndex: 2,
        }}
        aria-label="Next image"
      >
        ›
      </button>

      {/* Dot indicators */}
      <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 12 }}>
        {CAROUSEL_IMAGES.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            style={{
              width: i === idx ? 20 : 6,
              height: 6,
              borderRadius: 999,
              background: i === idx ? "#f59e0b" : "rgba(0,0,0,0.18)",
              border: "none",
              cursor: "pointer",
              padding: 0,
              transition: "all 0.35s ease",
            }}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

function Hero({ onBook, onDateSelect }) {
  const ease = [0.22, 1, 0.36, 1];

  return (
    <>
      <section className="bg-white overflow-hidden" style={{ paddingTop: 64 /* navbar height */ }}>
        <div className="max-w-7xl mx-auto w-full px-5 sm:px-8 pt-10 pb-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">

          {/* ── LEFT: Text ── */}
          <div>
            {/* Eyebrow */}
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05, ease }}
              className="font-black tracking-[0.18em] uppercase mb-5 flex flex-wrap items-baseline gap-x-1 gap-y-0"
            >
              <span className="text-black text-base">HYDERABAD ❤️</span>
              <span className="text-[#f59e0b] text-[11px]">'S FIRST OVAL CRICKET GROUND</span>
            </motion.p>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, delay: 0.14, ease }}
              className="text-black font-black leading-[1.0] tracking-[-0.03em] mb-5"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: "clamp(3rem, 6.5vw, 5rem)",
              }}
            >
              Town Square<br />Arena
            </motion.h1>

            {/* Tagline */}
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.21, ease }}
              className="text-base sm:text-[17px] italic font-medium tracking-wide mb-5 -mt-2"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", color: "#f59e0b" }}
            >
              The Arena Where Legends Are Made
            </motion.p>

            {/* Subtext */}
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.26, ease }}
              className="text-black/50 text-base sm:text-lg leading-relaxed mb-8 max-w-sm"
            >
              Oval Cricket • Box Cricket • Pickleball —{" "}
              <span className="text-black/75 font-medium">Book your slot instantly.</span>
            </motion.p>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.38, ease }}
              className="mb-7"
            >
              <button
                onClick={onBook}
                className="bg-[#f59e0b] hover:bg-amber-400 text-black font-black text-base px-9 py-3.5 rounded-2xl transition-all duration-200 hover:shadow-xl hover:shadow-[#f59e0b]/30 hover:-translate-y-0.5 active:translate-y-0"
              >
                Book Now →
              </button>
            </motion.div>

            {/* Stat pills */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5, ease }}
              className="flex flex-wrap gap-2"
            >
              {[
                { icon: "🏟️", label: "1 Oval Ground" },
                { icon: "🏏", label: "2 Box Courts" },
                { icon: "🏓", label: "2 Pickleball Courts" },
              ].map((pill) => (
                <div
                  key={pill.label}
                  className="flex items-center gap-1.5 bg-black/[0.04] border border-black/8 rounded-full px-3.5 py-1.5 text-sm font-semibold text-black/60"
                >
                  <span className="text-sm leading-none">{pill.icon}</span>
                  {pill.label}
                </div>
              ))}
            </motion.div>
          </div>

          {/* ── RIGHT: Image carousel with gold border ── */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.18, ease }}
            className="flex items-center justify-center lg:justify-end"
          >
            <HeroCarousel />
          </motion.div>

        </div>
      </section>

      {/* ── 14-day availability strip ── */}
      <AvailabilityStrip onDateSelect={onDateSelect} />

      {/* ── Trust signals bar ── */}
      <div className="bg-[#fafafa] border-b border-black/6">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-2.5 flex flex-wrap items-center justify-center sm:justify-between gap-x-8 gap-y-1.5">
          {[
            { icon: "⚡", label: "Instant Confirmation" },
            { icon: "📱", label: "WhatsApp Updates" },
            { icon: "❌", label: "Free Cancellation" },
            { icon: "🔒", label: "Secure Payment" },
          ].map((t) => (
            <div key={t.label} className="flex items-center gap-1.5 text-black/45 text-xs font-semibold">
              <span>{t.icon}</span>
              {t.label}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function Courts({ onBook }) {
  const { pricing } = usePricing();
  return (
    <section id="courts" className="pt-24 pb-0 px-5 bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.div {...fadeUp()} className="mb-14">
          <p className="text-[#f59e0b] text-xs font-bold tracking-[0.2em] uppercase mb-3">Our Courts</p>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <h2
              className="text-black text-4xl sm:text-5xl font-black leading-tight tracking-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Three Sports,<br />One Address
            </h2>
            <p className="text-black/50 text-sm max-w-xs leading-relaxed">
              Each court is professionally maintained and available from 5:00 AM to 2:00 AM (next day), every day.
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {COURTS.map((court, i) => (
            <motion.div
              key={court.name}
              {...fadeUp(i * 0.1)}
              className="group rounded-3xl overflow-hidden border border-black/6 hover:shadow-2xl hover:shadow-black/10 transition-all duration-500 hover:-translate-y-1 bg-white flex flex-col"
            >
              {/* Image */}
              <div className="relative overflow-hidden flex-shrink-0" style={{ height: 220 }}>
                <img
                  src={court.image}
                  alt={court.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  style={{}}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                {/* Court count badge — bottom left */}
                <div className="absolute bottom-4 left-4">
                  <span className="text-xs font-bold text-white/80 uppercase tracking-widest">{court.tag}</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-black text-black text-xl mb-2 tracking-tight">{court.name}</h3>
                <p className="text-black/50 text-sm leading-relaxed mb-4">{court.desc}</p>

                {/* Bullet points */}
                {court.features.length > 0 && (
                  <ul className="space-y-1.5 mb-5">
                    {court.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-black/60">
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-[#f59e0b]" />
                        {f}
                      </li>
                    ))}
                  </ul>
                )}

                {/* Note box */}
                {court.note && (
                  <div className="rounded-xl bg-amber-50 border border-amber-100 px-4 py-3 mb-5">
                    <p className="text-[#b45309] text-xs leading-relaxed">
                      <span className="font-bold">Note: </span>{court.note}
                    </p>
                  </div>
                )}

                {/* Price + CTA */}
                <div className="mt-auto pt-4 border-t border-black/6 flex items-center justify-between">
                  <div>
                    <div className="text-black/40 text-xs mb-0.5">Starting</div>
                    <div className="text-xl leading-none">
                      <span className="text-black font-black">₹{Number(pricing[court.courtKey]?.weekdayDay ?? 800).toLocaleString()}</span><span className="text-black/35 text-sm font-medium">/hr</span>
                    </div>
                  </div>
                  <button
                    onClick={onBook}
                    className="bg-black text-white font-bold text-sm px-5 py-2.5 rounded-xl transition-all duration-200 hover:bg-[#f59e0b] hover:text-black"
                  >
                    Book This Court
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

const GALLERY_IMAGES = [
  { src: img1647, label: "The Facility" },
  { src: img1649, label: "Playing Area" },
  { src: img1650, label: "Courts" },
  { src: img1653, label: "Arena View" },
  { src: img1654, label: "Ground" },
  { src: img1662, label: "Facilities" },
  { src: img1663, label: "Sports Area" },
  { src: img1664, label: "Town Square Arena" },
];

function Gallery() {
  return (
    <section id="gallery" className="py-24 px-5 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div {...fadeUp()} className="mb-12">
          <p className="text-[#f59e0b] text-xs font-bold tracking-[0.2em] uppercase mb-3">Our Facility</p>
          <h2
            className="text-black text-4xl sm:text-5xl font-black leading-tight tracking-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Inside Town Square Arena
          </h2>
        </motion.div>

        {/* Uniform grid */}
        <motion.div
          {...fadeUp(0.1)}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
        >
          {GALLERY_IMAGES.map((img, i) => (
            <div
              key={i}
              className="relative overflow-hidden rounded-2xl group cursor-pointer"
              style={{ paddingTop: "56.25%" /* 16:9 */ }}
            >
              <img
                src={img.src}
                alt={img.label}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/45 transition-all duration-400 flex items-end p-4">
                <span className="text-white font-bold text-sm opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                  {img.label}
                </span>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="py-24 px-5 bg-[#fafafa]">
      <div className="max-w-5xl mx-auto">
        <motion.div {...fadeUp()} className="text-center mb-16">
          <p className="text-[#f59e0b] text-xs font-bold tracking-[0.2em] uppercase mb-3">How It Works</p>
          <h2
            className="text-black text-4xl sm:text-5xl font-black tracking-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Ready to play in 3 steps
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 relative">
          {/* Connector line (desktop only) */}
          <div className="hidden sm:block absolute top-10 left-[16.67%] right-[16.67%] h-px bg-black/8" />

          {STEPS.map((step, i) => (
            <motion.div key={step.num} {...fadeUp(i * 0.12)} className="relative text-center">
              {/* Step number bubble */}
              <div className="relative inline-flex items-center justify-center mb-5">
                <div className="w-20 h-20 rounded-full bg-white border-2 border-black/8 flex items-center justify-center shadow-sm">
                  <span className="text-3xl">{step.icon}</span>
                </div>
                <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-[#f59e0b] text-black text-[10px] font-black flex items-center justify-center">
                  {step.num.replace("0", "")}
                </span>
              </div>
              <h3 className="font-black text-black text-lg mb-2">{step.title}</h3>
              <p className="text-black/50 text-sm leading-relaxed max-w-[220px] mx-auto">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  const { pricing } = usePricing();
  return (
    <section id="pricing" className="pt-24 pb-0 px-5 bg-white">
      <div className="max-w-4xl mx-auto">
        <motion.div {...fadeUp()} className="mb-14">
          <p className="text-[#f59e0b] text-xs font-bold tracking-[0.2em] uppercase mb-3">Rate Chart</p>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <h2
              className="text-black text-4xl sm:text-5xl font-black tracking-tight leading-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Our Rate Chart
            </h2>
            <div className="text-right">
              <p className="inline-block bg-[#f59e0b]/10 border border-[#f59e0b]/30 text-[#f59e0b] text-xs font-bold px-3 py-1.5 rounded-full">
                🎁 Book 4 hrs, pay for 3
              </p>
            </div>
          </div>
        </motion.div>

        {/* Rate table */}
        <motion.div {...fadeUp(0.1)} className="rounded-2xl border border-black/8 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] border-collapse">
              {/* Header */}
              <thead>
                <tr style={{ background: "#1a1a1a" }}>
                  <th className="text-left px-6 py-4 text-xs font-black text-white uppercase tracking-widest w-[30%]">Court</th>
                  <th className="text-center px-4 py-4">
                    <span className="text-xs font-black text-white uppercase tracking-widest block">☀️ Weekday Day</span>
                    <span className="text-[10px] font-normal text-white/40">6 AM – 6 PM</span>
                  </th>
                  <th className="text-center px-4 py-4">
                    <span className="text-xs font-black text-white uppercase tracking-widest block">🌙 Weekday Night</span>
                    <span className="text-[10px] font-normal text-white/40">6 PM – 6 AM</span>
                  </th>
                  <th className="text-center px-4 py-4">
                    <span className="text-xs font-black text-white uppercase tracking-widest block">☀️ Weekend Day</span>
                    <span className="text-[10px] font-normal text-white/40">6 AM – 6 PM</span>
                  </th>
                  <th className="text-center px-4 py-4">
                    <span className="text-xs font-black text-white uppercase tracking-widest block">🌙 Weekend Night</span>
                    <span className="text-[10px] font-normal text-white/40">6 PM – 6 AM</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {PRICING_ROWS.map((row, i) => {
                  const cp  = pricing[row.courtKey] || {};
                  const def = { weekdayDay: 800, weekdayNight: 1100, weekendDay: 1200, weekendNight: 1500 };
                  const wd  = Number(cp.weekdayDay   ?? def.weekdayDay);
                  const wdn = Number(cp.weekdayNight ?? def.weekdayNight);
                  const we  = Number(cp.weekendDay   ?? def.weekendDay);
                  const wen = Number(cp.weekendNight ?? def.weekendNight);
                  return (
                    <tr key={row.sport} className={`border-t border-black/6 ${i % 2 === 0 ? "bg-white" : "bg-[#fafafa]"}`}>
                      <td className="px-6 py-5">
                        <span className="font-black text-black text-sm">{row.sport}</span>
                      </td>
                      <td className="px-4 py-5 text-center">
                        <span className="font-black text-black text-lg">₹{wd.toLocaleString()}</span>
                        <span className="text-black/30 text-xs">/hr</span>
                      </td>
                      <td className="px-4 py-5 text-center">
                        <span className="font-black text-black text-lg">₹{wdn.toLocaleString()}</span>
                        <span className="text-black/30 text-xs">/hr</span>
                      </td>
                      <td className="px-4 py-5 text-center">
                        <span className="font-black text-[#f59e0b] text-lg">₹{we.toLocaleString()}</span>
                        <span className="text-[#f59e0b]/50 text-xs">/hr</span>
                      </td>
                      <td className="px-4 py-5 text-center">
                        <span className="font-black text-[#f59e0b] text-lg">₹{wen.toLocaleString()}</span>
                        <span className="text-[#f59e0b]/50 text-xs">/hr</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.p {...fadeUp(0.2)} className="text-black/35 text-sm text-center mt-6 leading-relaxed">
          Day rate applies from 6 AM to 6 PM. Night rate applies from 6 PM to 6 AM (next day) for floodlit play.
        </motion.p>
      </div>
    </section>
  );
}

function Location() {
  return (
    <section id="location" className="pt-16 pb-24 px-5 bg-[#fafafa]">
      <div className="max-w-7xl mx-auto">
        <motion.div {...fadeUp()} className="mb-12">
          <p className="text-[#f59e0b] text-xs font-bold tracking-[0.2em] uppercase mb-3">Find Us</p>
          <h2
            className="text-black text-4xl sm:text-5xl font-black tracking-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Right in the heart<br />of Hyderabad
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
          {/* Map */}
          <motion.div {...fadeUp(0.1)} className="lg:col-span-3 rounded-3xl overflow-hidden border border-black/8 shadow-sm">
            <iframe
              title="The Oval Location"
              src="https://maps.google.com/maps?q=Town+Square+Suites,+6-114/51/2/A,+Shamshabad+Mamidipally+Rd,+Shamshabad,+Hyderabad,+Telangana+501218&output=embed"
              width="100%"
              height="400"
              style={{ border: 0, display: "block" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>

          {/* Contact info */}
          <motion.div {...fadeUp(0.2)} className="lg:col-span-2 flex flex-col gap-5">
            {[
              {
                icon: "📍",
                label: "Address",
                value: "Town Square Suites, 6-114/51/2/A\nShamshabad - Mamidipally Rd, Shamshabad\nHyderabad, Telangana 501218",
              },
              {
                icon: "🕐",
                label: "Hours",
                value: "5:00 AM – 2:00 AM\nEvery day of the year",
              },
              {
                icon: "📞",
                label: "Phone",
                value: "+91 91333 03044",
              },
              {
                icon: "✉️",
                label: "Email",
                value: "support@townsquarearena.com",
              },
            ].map((item) => (
              <div key={item.label} className="bg-white rounded-2xl border border-black/6 p-5">
                <div className="flex items-start gap-4">
                  <span className="text-xl mt-0.5">{item.icon}</span>
                  <div>
                    <p className="text-xs font-bold text-black/40 uppercase tracking-widest mb-1">{item.label}</p>
                    <p className="text-black font-medium text-sm leading-relaxed whitespace-pre-line">{item.value}</p>
                  </div>
                </div>
              </div>
            ))}

            {/* WhatsApp */}
            <a
              href="https://wa.me/919133303044"
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2.5 bg-[#25D366] hover:bg-[#22c55e] text-white font-bold px-6 py-4 rounded-2xl transition-all hover:shadow-lg hover:shadow-green-500/20 hover:-translate-y-0.5"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              Chat on WhatsApp
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Reviews() {
  const REVIEWS = [
    { initial: "S", name: "Sai Krishna R", role: "Regular Member", rating: 5, text: "Mast hai, seriously! We booked the box cricket slot last Saturday evening — the turf condition was top notch and lights were bright enough for proper night cricket. Our gang from Kondapur plays here every weekend now." },
    { initial: "P", name: "Preetham", role: "Colony Tournament", rating: 5, text: "We played oval cricket here for our colony tournament — ground was proper size, pitch was well-maintained. Online booking was super easy, took barely 2 minutes on the phone. Staff were helpful and didn't give us any last-minute surprises on timing." },
    { initial: "H", name: "Hussain", role: "Cricket Player", rating: 5, text: "From booking to the final over, everything was smooth. Changing rooms were clean, and the ground was in excellent condition." },
  ];

  return (
    <section className="pt-16 pb-0 px-5 bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.div {...fadeUp()} className="mb-14">
          <p className="text-[#f59e0b] text-xs font-bold tracking-[0.2em] uppercase mb-3">Reviews</p>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <h2
              className="text-black text-4xl sm:text-5xl font-black tracking-tight leading-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Loved by Hyderabad's<br />sporting community
            </h2>
            <div className="flex items-center gap-2">
              <span className="text-[#f59e0b] text-lg">★★★★★</span>
              <span className="text-black/40 text-sm">4.9 from 340+ reviews</span>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {REVIEWS.map((r, i) => (
            <motion.div
              key={r.name}
              {...fadeUp(i * 0.1)}
              className="bg-[#fafafa] rounded-3xl border border-black/6 p-7 hover:shadow-xl hover:shadow-black/5 transition-all hover:-translate-y-1"
            >
              <div className="text-[#f59e0b] text-sm mb-4">{"★".repeat(r.rating)}</div>
              <p className="text-black/70 text-sm leading-relaxed mb-6 italic">"{r.text}"</p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-black flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {r.initial}
                </div>
                <div>
                  <p className="font-bold text-black text-sm">{r.name}</p>
                  <p className="text-black/40 text-xs">{r.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaStrip({ onBook }) {
  return (
    <section className="py-20 px-5 bg-black">
      <motion.div {...fadeUp()} className="max-w-3xl mx-auto text-center">
        <p className="text-[#f59e0b] text-xs font-bold tracking-[0.25em] uppercase mb-4">Weekend slots fill fast</p>
        <h2
          className="text-white text-4xl sm:text-6xl font-black tracking-tight leading-tight mb-8"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Ready to<br />hit the ground?
        </h2>
        <button
          onClick={onBook}
          className="bg-[#f59e0b] hover:bg-[#f59e0b]/90 text-black font-black text-lg px-12 py-5 rounded-2xl transition-all hover:shadow-2xl hover:shadow-[#f59e0b]/30 hover:-translate-y-0.5"
        >
          Book a Slot Now →
        </button>
      </motion.div>
    </section>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function Landing() {
  const navigate = useNavigate();
  const { setSelectedDate, clearSlots } = useBooking();

  const onBook = () => navigate("/book");

  const onDateSelect = (dateStr) => {
    setSelectedDate(dateStr);
    clearSlots();
    navigate("/book");
  };

  return (
    <div>
      <Hero onBook={onBook} onDateSelect={onDateSelect} />
      <Courts onBook={onBook} />
      <Gallery />
      <HowItWorks />
      <Pricing />
      <Reviews />
      <Location />
      <CtaStrip onBook={onBook} />
    </div>
  );
}
